import React from "react";
import { Link } from "react-router-dom";
import {
  Tag,
  Heading,
  Lead,
  BtnPrimary,
  BtnOutline,
  Card,
  IconBox,
  Section,
} from "../components/UI";
import StatsBar from "../components/StatsBar";
import styles from "./Home.module.css";

const SERVICES = [
  {
    icon: "🎓",
    title: "Individual & Group Training",
    desc: "Hands-on, expert-led courses across in-demand technologies — from Java Full Stack to AI & ML.",
  },
  {
    icon: "🏢",
    title: "Corporate Training Solutions",
    desc: "Tailored upskilling programs designed around your organization's technology stack and goals.",
  },
  {
    icon: "🚀",
    title: "Career-Focused Learning Paths",
    desc: "Certification prep, hands-on projects, and job-readiness support to accelerate your career.",
  },
];

const FEATURED_COURSES = ["☕ Java Full Stack", "🐍 Python", "🤖 AI & ML", "☁️ Salesforce", "🔄 DevOps", "📊 Power BI"];

const WHY = [
  {
    t: "Certified Expert Instructors",
    d: "Industry professionals with real-world experience teaching every course.",
  },
  {
    t: "Hands-On, Project-Based Learning",
    d: "Practical projects and case studies — not just theory.",
  },
  {
    t: "Customized Learning Paths",
    d: "Tailored programs for individuals and corporate teams alike.",
  },
  {
    t: "Flexible Online Delivery",
    d: "Learn from anywhere, on a schedule that fits your life.",
  },
  {
    t: "Cost-Effective Training",
    d: "High-value programs designed to maximize ROI for learners and organizations.",
  },
  {
    t: "Career Growth Support",
    d: "Certification prep, job readiness, and placement assistance.",
  },
];

export default function Home() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroGrid} />
        <div className={styles.heroInner}>
          {/* <div className={`${styles.heroBadge} animate-fade-up delay-1`}>
            Software · HR · Staffing · IT Training
          </div> */}
          <h1 className={`${styles.heroTitle} animate-fade-up delay-2`}>
            Empowering Business Through{" "}
            <em className={styles.heroEm}>People &amp; Technology</em>
          </h1>
          <p className={`${styles.heroSub} animate-fade-up delay-3`}>
            Metawave Solutions delivers individual, group, and corporate IT
            training — equipping learners with practical, industry-relevant
            skills to excel in today's dynamic technology landscape.
          </p>
          <div className={`${styles.heroBtns} animate-fade-up delay-4`}>
            <BtnPrimary to="/about-us">Learn More</BtnPrimary>
            <BtnOutline to="/contact-us">Get in Touch</BtnOutline>
          </div>
        </div>
      </div>

      {/* ── Stats ─────────────────────────────────────────────── */}
      <StatsBar />

      {/* ── About Snippet ─────────────────────────────────────── */}
      <Section variant="paper">
        <div className={styles.aboutGrid}>
          <div>
            <Tag>About Us</Tag>
            <Heading>Empowering Learners, Transforming Careers</Heading>
            <Lead>
              At Metawave Solutions, we're dedicated to transforming careers
              and organizations through individual, group, and corporate
              training programs — equipping learners with practical,
              industry-relevant skills to excel in today's dynamic technology
              landscape. We combine expert-led instruction, hands-on projects,
              and career-focused learning paths so every participant gains the
              confidence to succeed.
            </Lead>
            <div className={styles.aboutBtns}>
              <BtnPrimary to="/about-us">Learn More</BtnPrimary>
            </div>
          </div>
          <div className={styles.aboutVisual}>
            <div className={styles.aboutVisualBg} />
            <div className={styles.avPill}>Full-Range IT Training</div>
            <h3 className={styles.avTitle}>
              From Classroom to Career — We've Got You Covered
            </h3>
            <ul className={styles.avList}>
              {[
                "Individual & group training across 10 in-demand technologies",
                "Certified instructors with hands-on, project-based teaching",
                "Customized corporate training solutions",
                "Certification prep & career-readiness support",
                "Fully online — learn from anywhere",
              ].map((item, i) => (
                <li key={i}>
                  <span className={styles.avArrow}>→</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ── Training Solutions ────────────────────────────────── */}
      <Section variant="dark">
        <Tag light>What We Offer</Tag>
        <Heading light>Our Training Solutions</Heading>
        <Lead light>Individual, Group & Corporate Training Programs</Lead>
        <div className={styles.servicesGrid}>
          {SERVICES.map((svc, i) => (
            <div key={i} className={styles.svcCard}>
              <div className={styles.svcIcon}>{svc.icon}</div>
              <h3 className={styles.svcTitle}>{svc.title}</h3>
              <p className={styles.svcDesc}>{svc.desc}</p>
            </div>
          ))}
        </div>
      </Section>
      {/* ── Featured Courses ──────────────────────────────────── */}
      <Section variant="paper" className={styles.sectionCenter}>
        <Tag>Featured Courses</Tag>
        <Heading>Skills That Get You Hired</Heading>
        <div className={styles.indRow}>
          {FEATURED_COURSES.map((c, i) => (
            <Link key={i} to="/training" className={styles.indChip}>
              {c}
            </Link>
          ))}
        </div>
      </Section>

      {/* ── Why Us ────────────────────────────────────────────── */}
      <Section variant="dark">
        <Tag light>Why Choose Us</Tag>
        <Heading light>What Sets Metawave Apart</Heading>
        <div className={styles.whyGrid}>
          {WHY.map((w, i) => (
            <div key={i} className={styles.whyItem}>
              <div className={styles.whyCheck}>✓</div>
              <div>
                <h4 className={styles.whyTitle}>{w.t}</h4>
                <p className={styles.whyDesc}>{w.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── CTA Banner ────────────────────────────────────────── */}
      <div className={styles.ctaBanner}>
        <h2 className={styles.ctaTitle}>Ready to Work Together?</h2>
        <p className={styles.ctaSub}>
          Get in touch and our team will respond promptly.
        </p>
        <BtnPrimary to="/contact-us">Contact Us →</BtnPrimary>
      </div>
    </>
  );
}
